import { useToast } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { MessageTemplateTypes } from 'ai-commons'
import debounce from 'lodash.debounce'
import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useState } from 'react'
import { MESSAGE_TEMPLATES_QUERY_KEY } from '../components/MessageTemplates'
import {
  IMessageTemplate,
  MessageTemplateCtx,
} from '../providers/MessageTemplate.provider'
import { IMessageTemplateSchema } from '../schemas/message-template.schema'
import { useError } from './useError'

export const useMessageTemplateEdit = (props: {
  messageTemplate: IMessageTemplate
}) => {
  const router = useRouter()
  const messageTemplateType: MessageTemplateTypes =
    router.query?.flow || props.messageTemplate.type
  const formId = 'update-message-template'

  const error = useError()

  const queryClient = useQueryClient()

  const [isLoading, setIsLoading] = useState(false)

  const { updateMethod, updateStatusMethod, multiple } =
    useContext(MessageTemplateCtx)

  const toast = useToast()

  const onUpdateMessageTemplate = useCallback(
    (data: IMessageTemplateSchema, onSuccess: () => void) => {
      setIsLoading(true)

      updateMethod(props.messageTemplate._id, {
        ...data,
        type: messageTemplateType,
      })
        .then((res) => {
          queryClient.invalidateQueries({
            queryKey: [MESSAGE_TEMPLATES_QUERY_KEY],
          })

          onSuccess()
        })
        .catch((err) => {
          console.error('Error updating message template', err)
          error.handleError(err)
        })
        .finally(() => setIsLoading(false))
    },
    [messageTemplateType],
  )

  const [active, setActive] = useState<boolean>(
    props.messageTemplate.status === 'active',
  )

  const [statusTouched, setStatusTouched] = useState(false)

  const onChangeChecked = useCallback((checked: boolean) => {
    setActive(checked)
    setStatusTouched(true)
  }, [])

  const debounceSwitch = useCallback(debounce(onChangeChecked, 500), [])

  const onUpdateMessageTemplateStatus = useCallback((check: boolean) => {
    const data = {
      status: check ? 'active' : 'inactive',
      type: messageTemplateType,
    }

    updateStatusMethod(props.messageTemplate._id, data)
      .then((res) => {
        queryClient.invalidateQueries({
          queryKey: [MESSAGE_TEMPLATES_QUERY_KEY],
        })

        toast({
          isClosable: true,
          status: 'success',
          title: 'Estado de la plantilla de mensaje actualizado',
          description:
            'El estado de la plantilla de mensaje ha sido actualizado correctamente',
        })
      })
      .catch((err) => {
        console.error('Error updating message template status', err)
        error.handleError(err)
      })
  }, [])

  useEffect(() => {
    if (statusTouched) {
      onUpdateMessageTemplateStatus(active)
    }
  }, [active, statusTouched])

  return {
    onUpdateMessageTemplateStatus,
    debounceSwitch,
    isLoading,
    formId,
    onUpdateMessageTemplate,
  }
}
